import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LinkSimple, LinkBreak, PencilSimple, X, CheckCircle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Sözleşme eşleşme rozeti — auto-match sonucu + kullanıcı override etme imkânı.
 * `link` prop'u: { contract_id, contract_code, item_index, item_name, score, source }
 * `material` prop'u: {id, supplier_id, site_id, material_name} — override çağrısı için.
 * `onChange` prop'u: yeni link ile çağrılır (parent'ın state'ini güncellemek için).
 */
export default function ContractLinkChip({ link, material, onChange }) {
  const [open, setOpen] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCid, setSelectedCid] = useState("");
  const [selectedIdx, setSelectedIdx] = useState("");
  const [saving, setSaving] = useState(false);

  const scorePercent = link?.score ? Math.round(link.score * 100) : 0;
  const isManual = link?.source === "manual";
  const isHighConfidence = isManual || scorePercent >= 90;
  const isMedium = scorePercent >= 70 && scorePercent < 90;

  // Renk şeması
  let cls = "";
  if (!link) {
    cls = "bg-slate-100 text-slate-500 border-slate-300";
  } else if (isHighConfidence) {
    cls = "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100";
  } else if (isMedium) {
    cls = "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100";
  } else {
    // 60-69
    cls = "bg-orange-50 text-orange-800 border-orange-300 hover:bg-orange-100";
  }

  const openDialog = async () => {
    setOpen(true);
    setLoading(true);
    try {
      // Aynı ŞANTİYEdeki aktif sözleşmelerin tümü — tedarikçi filtresi YOK (Feb 2026).
      // Kullanıcı farklı firmadan aldığı malzemeyi de mevcut bir sözleşmeye manuel bağlayabilir.
      const params = { site_id: material?.site_id };
      const { data } = await axios.get(`${API}/contracts`, { params });
      setContracts(data || []);
      if (link?.contract_id) {
        setSelectedCid(link.contract_id);
        setSelectedIdx(String(link.item_index ?? -1));
      } else {
        setSelectedCid("");
        setSelectedIdx("");
      }
    } catch (e) {
      toast.error("Sözleşmeler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const save = async (payload) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/materials/${material.id}/contract-link`, payload);
      onChange?.(data.contract_link);
      toast.success(payload.cleared
        ? "Bağlantı kaldırıldı"
        : (payload.contract_id ? "Sözleşme bağlantısı manuel olarak ayarlandı" : "Auto-match'e döndü"));
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const selectedContract = contracts.find(c => c.id === selectedCid);
  const contractItems = selectedContract?.items || [];

  // Chip görünümü
  if (!link) {
    return (
      <>
        <button
          type="button"
          onClick={openDialog}
          data-testid={`chip-link-${material?.id}`}
          className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border ${cls} rounded-none sm:rounded-sm transition-colors`}
          title="Sözleşme bağlantısı yok — manuel eşle"
        >
          <LinkBreak size={10} weight="bold" />
          <span className="uppercase tracking-widest font-bold">Sözleşmesiz</span>
        </button>
        <LinkDialog
          open={open} onOpenChange={setOpen} loading={loading} saving={saving}
          contracts={contracts} contractItems={contractItems}
          selectedCid={selectedCid} setSelectedCid={setSelectedCid}
          selectedIdx={selectedIdx} setSelectedIdx={setSelectedIdx}
          currentLink={link} save={save} material={material}
        />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        data-testid={`chip-link-${material?.id}`}
        className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border ${cls} rounded-none sm:rounded-sm transition-colors group max-w-full`}
        title={`${link.contract_code} — ${link.item_name} (%${scorePercent}${isManual ? " · manuel" : ""})`}
      >
        <LinkSimple size={10} weight="bold" className="shrink-0" />
        <span className="font-mono font-bold truncate">{link.contract_code}</span>
        <span className="hidden sm:inline text-[9px] opacity-70 truncate max-w-[120px]">· {link.item_name}</span>
        <span className={`font-bold ${isHighConfidence ? "text-emerald-700" : isMedium ? "text-amber-700" : "text-orange-700"}`}>
          %{scorePercent}
        </span>
        {isManual && <CheckCircle size={10} weight="fill" className="text-emerald-600" title="Manuel doğrulanmış" />}
      </button>
      <LinkDialog
        open={open} onOpenChange={setOpen} loading={loading} saving={saving}
        contracts={contracts} contractItems={contractItems}
        selectedCid={selectedCid} setSelectedCid={setSelectedCid}
        selectedIdx={selectedIdx} setSelectedIdx={setSelectedIdx}
        currentLink={link} save={save} material={material}
      />
    </>
  );
}

function LinkDialog({ open, onOpenChange, loading, saving, contracts, contractItems,
                     selectedCid, setSelectedCid, selectedIdx, setSelectedIdx,
                     currentLink, save, material }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none sm:rounded-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkSimple size={18} weight="duotone" className="text-[#1B4776]" />
            Sözleşme Bağlantısı
          </DialogTitle>
          <DialogDescription className="text-xs">
            <span className="font-mono">{material?.material_name}</span> irsaliye kaydını bu şantiyedeki
            herhangi bir sözleşmeye manuel olarak bağla veya bağlantıyı kaldır.
            <span className="text-slate-500 block mt-0.5">
              Not: Farklı tedarikçilerden alınmış malzemeler de aynı sözleşmeye bağlanabilir.
            </span>
          </DialogDescription>
        </DialogHeader>

        {currentLink && (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded-none sm:rounded-sm p-2 mb-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">Mevcut Bağlantı</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-mono font-bold text-[#1B4776]">{currentLink.contract_code}</span>
              <span className="text-slate-400">·</span>
              <span>{currentLink.item_name}</span>
              <span className="text-slate-400">·</span>
              <span className={currentLink.source === "manual" ? "text-emerald-700 font-bold" : "text-slate-600"}>
                {currentLink.source === "manual" ? "Manuel" : `Auto %${Math.round(currentLink.score * 100)}`}
              </span>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Sözleşme</label>
            <Select value={selectedCid} onValueChange={(v) => { setSelectedCid(v); setSelectedIdx(""); }}>
              <SelectTrigger className="mt-1 rounded-none sm:rounded-sm h-9" data-testid="link-select-contract">
                <SelectValue placeholder={loading ? "Yükleniyor..." : "Sözleşme seç"} />
              </SelectTrigger>
              <SelectContent>
                {contracts.length === 0 && !loading && (
                  <div className="text-xs text-slate-500 px-2 py-3 text-center">
                    Bu şantiye için aktif sözleşme yok.
                  </div>
                )}
                {contracts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-[#1B4776]">{c.code || "(kodsuz)"}</span>
                      <span className="text-xs text-slate-600">{c.material_name || `${(c.items || []).length} kalem`}</span>
                      {c.supplier_name && (
                        <span className="text-[9px] uppercase tracking-widest px-1 py-0 border border-slate-300 text-slate-500 bg-slate-50">
                          {c.supplier_name}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedCid && contractItems.length > 0 && (
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Kalem</label>
              <Select value={selectedIdx} onValueChange={setSelectedIdx}>
                <SelectTrigger className="mt-1 rounded-none sm:rounded-sm h-9" data-testid="link-select-item">
                  <SelectValue placeholder="Kalem seç" />
                </SelectTrigger>
                <SelectContent>
                  {contractItems.map((it, i) => (
                    <SelectItem key={i} value={String(i)}>
                      <span>{it.material_name}</span>
                      <span className="ml-2 text-xs text-slate-500">· {it.quantity} {it.unit}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200 mt-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => save({ cleared: true })}
            disabled={saving}
            data-testid="link-btn-clear"
            className="h-9 rounded-none sm:rounded-sm border-red-300 text-red-700 hover:bg-red-50 text-[11px] uppercase tracking-widest font-bold"
          >
            <LinkBreak size={12} className="mr-1" /> Bağlantı Yok
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => save({})}
              disabled={saving}
              data-testid="link-btn-auto"
              className="h-9 rounded-none sm:rounded-sm text-slate-600 hover:bg-slate-100 text-[11px] uppercase tracking-widest font-bold"
              title="Manuel override'ı temizle, otomatik eşleşmeyi kullan"
            >
              Otomatik
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (!selectedCid) { toast.warning("Sözleşme seç"); return; }
                const payload = { contract_id: selectedCid };
                if (contractItems.length > 0) {
                  if (selectedIdx === "") { toast.warning("Kalem seç"); return; }
                  payload.item_index = parseInt(selectedIdx);
                } else {
                  payload.item_index = -1;
                }
                save(payload);
              }}
              disabled={saving || !selectedCid}
              data-testid="link-btn-save"
              className="h-9 rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white text-[11px] uppercase tracking-widest font-bold"
            >
              <CheckCircle size={12} className="mr-1" /> Kaydet
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
